import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import moment from 'moment';
import {
  ISecurityCheck,
  Personaldaten,
} from '../../../shared/model/personaldaten.model';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { AsyncPipe, DatePipe } from '@angular/common';
import { Berater, IBerater } from '../../../shared/model/berater.model';
import { BeraterService } from '../../../services/berater.service';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { FieldHintComponent } from '../../../shared/components/field-hint/field-hint.component';
import {
  MatError,
  MatFormField,
  MatLabel,
  MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import {
  MatDatepicker,
  MatDatepickerActions,
  MatDatepickerApply,
  MatDatepickerCancel,
  MatDatepickerInput,
  MatDatepickerToggle,
} from '@angular/material/datepicker';
import {
  MAT_SELECT_SCROLL_STRATEGY,
  MatSelect,
} from '@angular/material/select';
import {
  createCloseScrollStrategy,
  ScrollStrategy,
} from '@angular/cdk/overlay';
import { MatOption } from '@angular/material/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TravelWillingnessPipe } from '../../../shared/travel-willingness.pipe';
import { NationalityDisplayLabelPipe } from '../../../shared/nationality-display-label.pipe';
import { MatCard } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { YearDatePickerComponent } from '../../../shared/components/year-date-picker/year-date-picker.component';
import { MainFocusDropdownComponent } from './dropdowns/main-focus-dropdown/main-focus-dropdown.component';
import { IndustriesDropdownComponent } from './dropdowns/industries-dropdown/industries-dropdown.component';
import { Observable, Subject } from 'rxjs';
import { Requests, RequestType } from '../../../shared/model/request.model';
import { MessageService } from '../../../shared/components/message.service';
import { DateService } from '../../../services/date.service';
import { RequestsService } from '../../../services/requests.service';
import { takeUntil } from 'rxjs/operators';
import { VocabularyPipe } from '../../../shared/vocabulary.pipe';
import { InputUtils } from '../../../../utils/input-utils.component';
import { INPUT_LIMITS } from '../../../shared/constants/input-limits';
import { UserRoleService } from '../../../services/userRole.service';
import { UserAuthoritiesEnum } from '../../../shared/model/user.model';
import { MatDivider } from '@angular/material/list';
import { Badge, Subheadline } from '@spirit-testing/component-library';
import { MatCheckbox } from '@angular/material/checkbox';
import { CiSelectComponent } from '../../../../profiler-theme';
import {
  LanguageEntry,
  normalizeToLanguageEntries,
  sortLanguageEntries,
} from '../../../shared/constants/language-levels';
import { LanguageLevelsComponent } from './language-levels/language-levels.component';
import { languageDisplayName } from '../../../shared/constants/languages';
import { extractConstraintViolations } from '../../../shared/errors/constraint-violations';
import * as nationalities from 'i18n-nationality';
import deNationalityLocale from 'i18n-nationality/langs/de.json';
import enNationalityLocale from 'i18n-nationality/langs/en.json';
import { isKnownNationality } from '../../../shared/nationality-util';
import { isSecurityCheckExpired } from '../../../shared/model/personal-data-validation';
import { toYearDate } from './year-only-date';
import { MAX_TWO_PAGER_INDUSTRIES } from '../../../shared/constants/two-pager-limits';
import { FieldWarningComponent } from '../../../shared/components/field-warning/field-warning.component';
import {
  ValidationFinding,
  findEntryFinding,
  findFieldFinding,
  findValueFinding,
} from '../../../shared/model/validation-finding';

// Close the mat-select panel on page scroll instead of Material's default
// "reposition", which shrank/overlapped an upward-opening panel (NP20-706).
// threshold: 50 = px of scroll grace before the panel closes.
export function personalDataSelectScrollStrategy(
  injector: Injector,
): () => ScrollStrategy {
  return () => createCloseScrollStrategy(injector, { threshold: 50 });
}

const UNKNOWN_LANGUAGE_KEY = 'profileLanguages.errors.unknownLanguage';
const SENT_LANGUAGE_FIELD = /^sprachen\[(\d+)\]\.language$/;
@Component({
  selector: 'app-personal-data',
  standalone: true,
  imports: [
    DatePipe,
    ReactiveFormsModule,
    MatButton,
    MatLabel,
    MatInput,
    MatFormField,
    MatError,
    MatDatepicker,
    MatDatepickerActions,
    MatDatepickerApply,
    MatDatepickerCancel,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepickerInput,
    MatSelect,
    MatOption,
    TranslatePipe,
    TravelWillingnessPipe,
    MatCard,
    YearDatePickerComponent,
    MatIcon,
    MatIconButton,
    MainFocusDropdownComponent,
    AsyncPipe,
    IndustriesDropdownComponent,
    MatDivider,
    Subheadline,
    Badge,
    MatCheckbox,
    CiSelectComponent,
    NationalityDisplayLabelPipe,
    MatTooltip,
    FieldHintComponent,
    FieldWarningComponent,
    VocabularyPipe,
    LanguageLevelsComponent,
  ],
  templateUrl: './personal-data.component.html',
  providers: [
    {
      provide: MAT_SELECT_SCROLL_STRATEGY,
      useFactory: personalDataSelectScrollStrategy,
      deps: [Injector],
    },
  ],
  styleUrl: './personal-data.component.scss',
})
export class PersonalDataComponent implements OnInit, OnChanges, OnDestroy {
  @Input() personalData: Personaldaten;
  @Input() consultant: Berater;
  @Input() isApplicant: boolean = false;
  @Input() exportMode: boolean;
  @Input() twoPagerMode: boolean = false;
  @Input() forcedSchwerpunkte: string[] | null = null;
  @Input() forcedBranchen: string[] | null = null;
  @Input() findings: ValidationFinding[] = [];

  @ViewChild(LanguageLevelsComponent)
  private languageLevelsRef?: LanguageLevelsComponent;

  @Output() exportSelectionChange = new EventEmitter<string[]>();
  // NP20-958: Branchen sind im Two-Pager einzeln waehlbar und landen als eigener Block
  // unter LANGUAGES in der Ausgabe.
  @Output() industriesExportSelectionChange = new EventEmitter<string[]>();
  // NP20-828: Nach einem erfolgreichen Speichern reicht die Komponente den vom BE
  // zurueckgelieferten Berater direkt hoch. Der Parent uebernimmt ihn immutable in
  // selectedBerater, sodass die Section-Badges neu berechnet werden — kein Refetch,
  // damit parallel laufende Bearbeiten-Modi anderer Sektionen ihre Formulare nicht
  // unter den Haenden verlieren.
  @Output() saved = new EventEmitter<IBerater>();

  selectedSchwerpunkte: string[] = [];
  selectedBranchen: string[] = [];

  readonly MAX_SCHWERPUNKTE_EXPORT = 5;

  rbOptions = [
    { label: 'userlist.filter.travelYes', value: 'Ja' },
    { label: 'userlist.filter.travelNo', value: 'Nein' },
    { label: 'userlist.filter.travelRestricted', value: 'Eingeschränkt' },
  ];

  securityOptions = [
    { code: 'UE1', label: 'Ü1' },
    { code: 'UE2', label: 'Ü2' },
    { code: 'UE3', label: 'Ü3' },
  ];

  isSaving = false;

  personalForm!: FormGroup;
  protected get LIMITS() {
    return INPUT_LIMITS;
  }
  editMode = false;
  personalDataCopy: Personaldaten;
  originalPersonalData: Personaldaten;
  hasUnsavedChanges: boolean = false;

  nationalityOptions: { code: string; label: string }[] = [];
  nationalityCodes: string[] = [];
  private nationalityLabelByCode: Record<string, string> = {};

  displayNationality = (code: string): string =>
    this.nationalityLabelByCode[code] ?? code;

  // NP20-828: In der View wird ein branche/schwerpunkt als Legacy markiert, wenn der
  // gespeicherte Name nicht mehr im aktuellen Katalog existiert. Set wird beim
  // Aufloesen der Observables gefuellt (siehe ngOnInit).
  private knownIndustryNames = new Set<string>();
  private knownMainFocusNames = new Set<string>();
  private nationalityLabelToCode: Record<string, string> = {};
  currentNationalityLang = 'de';

  get sprachenEntries(): LanguageEntry[] {
    return normalizeToLanguageEntries(this.personalData?.sprachen ?? []);
  }

  get missingLevelCount(): number {
    return this.sprachenEntries.filter((entry) => !entry.level).length;
  }

  // Eine ungültige Sprachgruppe sperrt Speichern nicht: erst der Klick zeigt
  // Feld- und Gruppenfehler und setzt den Fokus.
  get saveDisabled(): boolean {
    if (!this.editMode || !this.personalForm) return false;
    const languagesInvalid = !!this.personalForm.get('sprachen')?.invalid;
    const otherInvalid = Object.entries(this.personalForm.controls).some(
      ([name, control]) => name !== 'sprachen' && control.invalid,
    );
    return (
      (!this.hasUnsavedChanges && !languagesInvalid) ||
      otherInvalid ||
      !this.hasIndustries ||
      !this.hasMainFocuses ||
      this.industriesLimitExceeded ||
      this.mainFocusesLimitExceeded
    );
  }
  private destroy$ = new Subject<void>();
  availableMainFocuses$!: Observable<Requests[]>;
  availableIndustries$!: Observable<Requests[]>;

  readonly MAX_INDUSTRIES = 7;
  readonly MAX_MAINFOCUS = 5;

  get industriesLimitExceeded(): boolean {
    const value =
      this.personalForm?.get('branchen')?.value ?? this.personalData?.branchen;
    return (value?.length ?? 0) > this.MAX_INDUSTRIES;
  }

  get mainFocusesLimitExceeded(): boolean {
    const value =
      this.personalForm?.get('schwerpunkte')?.value ??
      this.personalData?.schwerpunkte;
    return (value?.length ?? 0) > this.MAX_MAINFOCUS;
  }

  get hasIndustries(): boolean {
    return (
      Array.isArray(this.personalData?.branchen) &&
      this.personalData.branchen.length > 0
    );
  }

  get hasMainFocuses(): boolean {
    return (
      Array.isArray(this.personalData?.schwerpunkte) &&
      this.personalData.schwerpunkte.length > 0
    );
  }

  get saveBlockedTooltip(): string {
    if (!this.editMode) return '';
    if (!this.hasIndustries)
      return this.translateService.instant(
        'personalData.error.industriesRequired',
      );
    if (!this.hasMainFocuses)
      return this.translateService.instant(
        'personalData.error.mainFocusRequired',
      );
    return '';
  }

  get selectedSecurityTypes(): string[] {
    const securityArray = this.personalForm?.get('securityCheck') as FormArray;
    if (securityArray) {
      return securityArray.controls
        .map((ctrl) => ctrl.get('typ')?.value)
        .filter(Boolean);
    }
    return this.personalData?.securityCheck?.map((sc) => sc.typ) || [];
  }

  fieldFinding(field: string): ValidationFinding | null {
    if (this.editMode || this.exportMode) return null;
    return findFieldFinding(this.findings, field);
  }

  valueFinding(field: string, value: string): ValidationFinding | null {
    if (this.editMode || this.exportMode) return null;
    return findValueFinding(this.findings, field, value);
  }

  entryFinding(entryIndex: number, field: string): ValidationFinding | null {
    if (this.editMode || this.exportMode) return null;
    return findEntryFinding(this.findings, entryIndex, field);
  }

  // NP20-828: Legacy-Nationalitaet erkennt einen gespeicherten Wert, der sich nicht
  // mehr auf einen bekannten ISO-Code / eine i18n-Label-Uebereinstimmung aufloesen
  // laesst — der Nutzer sieht das sonst erst beim Wechsel in den Bearbeiten-Modus.
  isLegacyNationality(value: string | null | undefined): boolean {
    return !!value && !isKnownNationality(value);
  }

  // Solange der Katalog noch nicht geladen ist (Set leer), wird defensiv nichts als
  // Legacy markiert — sonst wuerden beim ersten Render alle Eintraege kurz rot blinken.
  isLegacyIndustry(name: string): boolean {
    return (
      this.knownIndustryNames.size > 0 && !this.knownIndustryNames.has(name)
    );
  }

  isLegacyMainFocus(name: string): boolean {
    return (
      this.knownMainFocusNames.size > 0 && !this.knownMainFocusNames.has(name)
    );
  }

  get hasLegacyIndustries(): boolean {
    return (this.personalData?.branchen ?? []).some((b) =>
      this.isLegacyIndustry(b),
    );
  }

  get hasLegacyMainFocuses(): boolean {
    return (this.personalData?.schwerpunkte ?? []).some((s) =>
      this.isLegacyMainFocus(s),
    );
  }

  get canSeeEntryDate(): boolean {
    return this.userRoleService.includesRoles([
      UserAuthoritiesEnum.OFFICE,
      UserAuthoritiesEnum.SALE,
      UserAuthoritiesEnum.ADMIN,
    ]);
  }

  get hireDateRequired(): boolean {
    return this.canSeeEntryDate && !this.isApplicant;
  }

  private updateEntryDateValidators(): void {
    const ctrl = this.personalForm?.get('eintrittsdatum');
    if (!ctrl) return;
    if (this.hireDateRequired) {
      ctrl.setValidators(Validators.required);
    } else {
      ctrl.clearValidators();
    }
    ctrl.updateValueAndValidity();
  }

  // NP20-876: Reine Whitespace-Eingaben (Leerzeichen/Tabs) sollen im Begründungsfeld nicht
  // als gültig zählen — sonst umgeht der Nutzer den Required-Check aus NP20-839.
  // Meldet denselben `required`-Fehler, damit die bestehende i18n-Fehlermeldung greift.
  static requiredNonBlank(control: AbstractControl): ValidationErrors | null {
    const value = (control.value ?? '') as string;
    return value.trim().length === 0 ? { required: true } : null;
  }

  // trim-basiert: leerer Wert wird an requiredNonBlank delegiert.
  static minLengthTrimmed(min: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = ((control.value ?? '') as string).trim();
      if (value.length === 0) return null;
      return value.length < min
        ? { minlength: { requiredLength: min, actualLength: value.length } }
        : null;
    };
  }

  // NP20-839: Bei Reisebereitschaft "eingeschränkt" ist eine Begründung Pflicht,
  // sonst nur die Längenbegrenzung (NP20-861). maxLength bleibt in beiden Fällen erhalten.
  private updateEinschraenkungValidators(): void {
    const ctrl = this.personalForm?.get('einschraenkung');
    if (!ctrl) return;
    const validators = [Validators.maxLength(INPUT_LIMITS.FREETEXT)];
    if (this.personalForm.get('reisebereitschaft')?.value === 'Eingeschränkt') {
      validators.push(PersonalDataComponent.requiredNonBlank);
      validators.push(
        PersonalDataComponent.minLengthTrimmed(INPUT_LIMITS.LIMITED_REASON_MIN),
      );
    }
    ctrl.setValidators(validators);
    ctrl.updateValueAndValidity();
  }

  constructor(
    private fb: FormBuilder,
    private beraterService: BeraterService,
    private messageService: MessageService,
    private dateService: DateService,
    private requestService: RequestsService,
    private userRoleService: UserRoleService,
    private translateService: TranslateService,
  ) {}

  private loadNationalityOptions(lang: string): void {
    const supportedLang = nationalities.langs().includes(lang) ? lang : 'de';
    this.currentNationalityLang = supportedLang;

    const names = nationalities.getNames(supportedLang);
    this.nationalityOptions = Object.entries(names)
      .map(([code, label]) => ({
        code,
        label: (label as string).toLowerCase(),
      }))
      .sort((a, b) => a.label.localeCompare(b.label, supportedLang));
    this.nationalityCodes = this.nationalityOptions.map((o) => o.code);
    this.nationalityLabelByCode = Object.fromEntries(
      this.nationalityOptions.map((o) => [o.code, o.label]),
    );
  }

  ngOnInit() {
    nationalities.registerLocale(deNationalityLocale);
    nationalities.registerLocale(enNationalityLocale);

    // Build label→code map once, keyed by ISO code to avoid label collisions across languages.
    for (const lang of nationalities.langs()) {
      const allNames = nationalities.getNames(lang);
      for (const [code, label] of Object.entries(allNames)) {
        this.nationalityLabelToCode[(label as string).toLowerCase()] = code;
      }
    }

    const currentLang = this.translateService.currentLang ?? 'de';
    this.loadNationalityOptions(currentLang);

    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ lang }) => {
        this.loadNationalityOptions(lang);
      });

    // NP20-828: Gemeinsamer shareReplay-Stream im RequestsService, den auch
    // ConsultantComponent nutzt — nur ein HTTP-Roundtrip pro Katalog. Sortiert
    // wird in den Dropdowns nach dem angezeigten Namen (NP20-1170).
    this.availableMainFocuses$ = this.requestService.getApproved$(
      RequestType.MAINFOCUS,
    );
    this.availableIndustries$ = this.requestService.getApproved$(
      RequestType.INDUSTRY,
    );

    // Auf resolved-Werte hoeren, damit isLegacyIndustry/isLegacyMainFocus
    // die aktuellen Katalog-Namen kennt.
    this.availableIndustries$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (items) =>
          (this.knownIndustryNames = new Set(items.map((i) => i.name))),
      );
    this.availableMainFocuses$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (items) =>
          (this.knownMainFocusNames = new Set(items.map((i) => i.name))),
      );
    const initial = this.toFormValue(this.personalData);

    this.personalForm = this.fb.group({
      position: [
        initial.position,
        [Validators.required, Validators.maxLength(INPUT_LIMITS.SHORT_LABEL)],
      ],
      eintrittsdatum: [
        initial.eintrittsdatum,
        this.hireDateRequired ? Validators.required : [],
      ],
      beraterSeit: [
        initial.beraterSeit,
        [Validators.required, Validators.min(1900)],
      ],
      verfuegbar: [initial.verfuegbar, Validators.required],
      wohnort: [
        initial.wohnort,
        [Validators.required, Validators.maxLength(INPUT_LIMITS.LOCATION)],
      ],
      nationalitaet: [
        initial.nationalitaet,
        [
          Validators.required,
          (control) => {
            if (!control.value) return null;
            const valid = this.nationalityOptions.some(
              (o) => o.code === control.value,
            );
            return valid ? null : { invalidOption: true };
          },
        ],
      ],
      jahrgang: [initial.jahrgang, Validators.required],
      reisebereitschaft: [initial.reisebereitschaft, Validators.required],
      einschraenkung: [
        initial.einschraenkung,
        initial.reisebereitschaft === 'Eingeschränkt'
          ? [
              PersonalDataComponent.requiredNonBlank,
              PersonalDataComponent.minLengthTrimmed(
                INPUT_LIMITS.LIMITED_REASON_MIN,
              ),
              Validators.maxLength(INPUT_LIMITS.FREETEXT),
            ]
          : [Validators.maxLength(INPUT_LIMITS.FREETEXT)],
      ],
      sprachen: [
        initial.sprachen,
        (ctrl: AbstractControl) => {
          const entries: LanguageEntry[] = ctrl.value ?? [];
          if (entries.length === 0) return { required: true };
          const incomplete = entries.some((e) => !e.language || !e.level);
          if (incomplete) return { incompleteEntries: true };
          return null;
        },
      ],
      branchen: [
        initial.branchen,
        [
          Validators.required,
          this.maxLengthArrayValidator(this.MAX_INDUSTRIES),
        ],
      ],
      schwerpunkte: [
        initial.schwerpunkte,
        [Validators.required, this.maxLengthArrayValidator(this.MAX_MAINFOCUS)],
      ],
      securityCheck: this.fb.array(
        this.personalData.securityCheck?.map((securityCheck) =>
          this.createSecurityCheckFormGroup(securityCheck),
        ) || [],
      ),
    });

    this.personalForm.valueChanges.subscribe(() => {
      if (this.editMode) {
        const currentValue = this.personalForm.value;

        this.hasUnsavedChanges =
          JSON.stringify(currentValue) !==
          JSON.stringify(this.personalDataCopy);
      }
    });

    // NP20-839: Pflicht-Validierung der Begründung an die Reisebereitschaft koppeln.
    this.personalForm
      .get('reisebereitschaft')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateEinschraenkungValidators());
  }

  private toFormValue(data?: Personaldaten) {
    return {
      position: data?.position || '',
      eintrittsdatum: data?.hireDate || '',
      // NP20-950: new Date(undefined) ergab ein Invalid Date, und das ist fuer
      // required eine Eingabe - ohne gespeichertes Jahr war der Button aktiv.
      beraterSeit: toYearDate(data?.beraterSeit),
      verfuegbar: data?.verfuegbar || '',
      wohnort: data?.wohnort || '',
      nationalitaet: this.toNationalityCode(data?.nationalitaet),
      jahrgang: this.parseJahrgang(data?.jahrgang),
      reisebereitschaft: data?.reisebereitschaft || '',
      einschraenkung: data?.einschraenkung || '',
      sprachen: normalizeToLanguageEntries(data?.sprachen ?? []),
      branchen: data?.branchen || [],
      schwerpunkte: data?.schwerpunkte || [],
    };
  }

  private toNationalityCode(stored?: string): string | null {
    if (!stored) return null;
    return nationalities.getName(stored.toUpperCase(), 'de')
      ? stored.toUpperCase()
      : (this.nationalityLabelToCode[stored.toLowerCase()] ?? null);
  }

  maxLengthArrayValidator(max: number) {
    return (control: AbstractControl) => {
      const value = control.value || [];
      return value.length > max ? { maxLengthExceeded: true } : null;
    };
  }

  createSecurityCheckFormGroup(sc: ISecurityCheck): FormGroup {
    const expired = this.isSecurityCheckExpired(sc.completedOn);
    const group = this.fb.group({
      typ: [sc.typ || null],
      submittedOn: [sc.submittedOn || null, expired ? [] : Validators.required],
      completedOn: [sc.completedOn || null],
    });

    group.get('completedOn')?.valueChanges.subscribe((value) => {
      const submittedOn = group.get('submittedOn');
      if (this.isSecurityCheckExpired(value)) {
        submittedOn?.clearValidators();
      } else {
        submittedOn?.setValidators(Validators.required);
      }
      submittedOn?.updateValueAndValidity();
    });

    return group;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['personalData'] && changes['personalData'].currentValue) {
      const data = changes['personalData'].currentValue;
      this.personalDataCopy = JSON.parse(JSON.stringify(data));
      this.hasUnsavedChanges = false;
    }
    if (changes['consultant'] || changes['isApplicant']) {
      this.updateEntryDateValidators();
    }
    if (changes['exportMode'] || changes['twoPagerMode']) {
      if (this.exportMode && !this.twoPagerMode) {
        this.autoSelectAll();
      } else {
        this.selectedSchwerpunkte = [];
        this.selectedBranchen = [];
        this.exportSelectionChange.emit([]);
        this.industriesExportSelectionChange.emit([]);
      }
    } else if (
      changes['personalData'] &&
      this.exportMode &&
      !this.twoPagerMode
    ) {
      this.autoSelectAll();
    }

    if (changes['forcedSchwerpunkte'] && this.forcedSchwerpunkte !== null) {
      this.selectedSchwerpunkte = [...this.forcedSchwerpunkte];
      this.exportSelectionChange.emit([...this.selectedSchwerpunkte]);
    }

    if (changes['forcedBranchen'] && this.forcedBranchen !== null) {
      this.selectedBranchen = [...this.forcedBranchen];
      this.industriesExportSelectionChange.emit([...this.selectedBranchen]);
    }
  }

  private autoSelectAll(): void {
    this.selectedSchwerpunkte = [...(this.personalData?.schwerpunkte ?? [])];
    this.selectedBranchen = [...(this.personalData?.branchen ?? [])];
    this.exportSelectionChange.emit([...this.selectedSchwerpunkte]);
    this.industriesExportSelectionChange.emit([...this.selectedBranchen]);
  }

  toggleSchwerpunktExport(name: string, checked: boolean): void {
    if (checked) {
      if (!this.canSelectSchwerpunkt(name)) return;
      if (!this.selectedSchwerpunkte.includes(name)) {
        this.selectedSchwerpunkte.push(name);
      }
    } else {
      this.selectedSchwerpunkte = this.selectedSchwerpunkte.filter(
        (s) => s !== name,
      );
    }
    this.exportSelectionChange.emit([...this.selectedSchwerpunkte]);
  }

  canSelectSchwerpunkt(name: string): boolean {
    if (this.selectedSchwerpunkte.includes(name)) return true;
    return this.selectedSchwerpunkte.length < this.MAX_SCHWERPUNKTE_EXPORT;
  }

  toggleBrancheExport(name: string, checked: boolean): void {
    if (checked) {
      if (!this.canSelectBranche(name)) return;
      if (!this.selectedBranchen.includes(name)) {
        this.selectedBranchen.push(name);
      }
    } else {
      this.selectedBranchen = this.selectedBranchen.filter((b) => b !== name);
    }
    this.industriesExportSelectionChange.emit([...this.selectedBranchen]);
  }

  canSelectBranche(name: string): boolean {
    if (this.selectedBranchen.includes(name)) return true;
    return this.selectedBranchen.length < MAX_TWO_PAGER_INDUSTRIES;
  }

  private parseJahrgang(value: string | undefined): moment.Moment | null {
    if (!value) return null;
    const fullDate = moment(value, 'DD.MM.YYYY', true);
    if (fullDate.isValid()) return fullDate;
    const yearOnly = moment(value, 'YYYY', true);
    if (yearOnly.isValid()) return yearOnly;
    return null;
  }

  setDates() {
    this.personalForm
      .get('beraterSeit')
      ?.setValue(toYearDate(this.personalData.beraterSeit));
    this.personalForm
      .get('jahrgang')
      ?.setValue(this.parseJahrgang(this.personalData.jahrgang));
  }

  save() {
    if (this.isSaving) return;

    this.personalForm.markAllAsTouched();
    this.languageLevelsRef?.showGroupErrors();

    if (!this.hasMainFocuses) {
      this.messageService.error('personalData.error.mainFocusRequired');
    }
    if (!this.hasIndustries) {
      this.messageService.error('personalData.error.industriesRequired');
    }
    if (
      !(this.hasMainFocuses && this.hasIndustries) ||
      this.personalForm.invalid
    )
      return;

    const formValue = this.personalForm.value;
    const sortedSprachen = sortLanguageEntries(
      formValue.sprachen ?? [],
      (code) =>
        languageDisplayName(
          code,
          (key) => this.translateService.instant(key),
          this.translateService.currentLang ?? 'de',
        ),
    );

    const updated = {
      ...this.personalData,
      ...formValue,
      sprachen: sortedSprachen,
    };
    if (updated.beraterSeit instanceof Date) {
      updated.beraterSeit = updated.beraterSeit.getFullYear().toString();
    }
    if (moment.isMoment(updated.jahrgang)) {
      updated.jahrgang = updated.jahrgang.format('DD.MM.YYYY');
    }
    updated.verfuegbar = this.dateService.toLocalDateString(updated.verfuegbar);
    if (updated.eintrittsdatum) {
      updated.hireDate = this.dateService.toLocalDateString(
        updated.eintrittsdatum,
      );
      updated.hireDate = updated.hireDate;
      delete updated.eintrittsdatum;
    }
    updated.securityCheck = (updated.securityCheck ?? []).map(
      (sc: ISecurityCheck) => ({
        ...sc,
        submittedOn: this.dateService.toLocalDateString(sc.submittedOn),
        completedOn: this.dateService.toLocalDateString(sc.completedOn),
      }),
    );
    if (typeof updated.einschraenkung === 'string') {
      updated.einschraenkung = updated.einschraenkung.replace(/^\s+/, '');
    }
    this.isSaving = true;
    this.beraterService
      .updatePersonaldaten(updated, this.consultant.id)
      .subscribe({
        next: (value: IBerater) => {
          this.isSaving = false;
          this.editMode = false;
          this.consultant = value;
          this.personalData = this.consultant.personaldaten;
          this.personalDataCopy = JSON.parse(JSON.stringify(this.personalData));
          this.hasUnsavedChanges = false;
          this.personalForm.patchValue(this.toFormValue(this.personalData));
          const securityArray = this.personalForm.get(
            'securityCheck',
          ) as FormArray;
          securityArray.clear();
          (this.personalData.securityCheck || []).forEach((sc) => {
            securityArray.push(this.createSecurityCheckFormGroup(sc));
          });
          this.messageService.success(
            'personalData.successUpdateMessage',
            undefined,
            undefined,
            { durationMs: 2500 },
          );
          this.setDates();
          this.saved.emit(value);
        },
        error: (err: unknown) => {
          this.isSaving = false;
          this.markUnknownLanguages(err, sortedSprachen);
          this.messageService.error('personalData.errorUpdateMessage');
        },
      });
  }

  private markUnknownLanguages(
    err: unknown,
    sentSprachen: LanguageEntry[],
  ): void {
    const codes = (extractConstraintViolations(err) ?? [])
      .filter((v) => v.message === UNKNOWN_LANGUAGE_KEY)
      .map((v) => SENT_LANGUAGE_FIELD.exec(v.field ?? ''))
      .map((match) =>
        match ? sentSprachen[Number(match[1])]?.language : undefined,
      )
      .filter((code): code is string => !!code);
    if (codes.length > 0) {
      this.languageLevelsRef?.markServerUnknownLanguages(codes);
    }
  }

  onIndustriesChange(updated: string[]) {
    this.personalData.branchen = updated;
    this.personalForm.get('branchen')?.setValue(updated);
    this.personalForm.get('branchen')?.markAsTouched();

    if (this.editMode) {
      if (!this.hasIndustries) {
        this.messageService.warning('personalData.error.industriesRequired');
        return;
      }
      this.hasUnsavedChanges = true;
    }
  }

  onMainFocusChange(updated: string[]) {
    this.personalData.schwerpunkte = updated;
    this.personalForm.get('schwerpunkte')?.setValue(updated);

    if (this.editMode) {
      if (!this.hasMainFocuses) {
        this.messageService.warning('personalData.error.mainFocusRequired');
        return;
      }
      this.hasUnsavedChanges = true;
    }
  }

  onToggleEdit(): void {
    if (this.editMode) {
      this.save();
    } else {
      this.originalPersonalData = JSON.parse(JSON.stringify(this.personalData));
      this.personalDataCopy = JSON.parse(JSON.stringify(this.personalData));

      this.editMode = true;
      this.hasUnsavedChanges = false;
      this.personalForm.markAllAsTouched();
    }
  }

  onCancelEdit() {
    this.personalData = JSON.parse(JSON.stringify(this.originalPersonalData));
    this.personalForm.reset({
      ...this.toFormValue(this.personalData),
      securityCheck: this.personalData.securityCheck || [],
    });

    const securityArray = this.personalForm.get('securityCheck') as FormArray;
    securityArray.clear();

    (this.personalData.securityCheck || []).forEach((sc) => {
      securityArray.push(this.createSecurityCheckFormGroup(sc));
    });

    this.setDates();
    this.editMode = false;
    this.hasUnsavedChanges = false;
  }

  // NP20-950: null heisst "kein Jahr". Der Wert muss dann wirklich geleert werden,
  // sonst haelt Validators.required den letzten Stand fuer eine Eingabe und der
  // Speichern-Button bleibt trotz leerem Feld aktiv.
  setYear(year: number | null, path: string) {
    this.personalForm
      .get(path)
      ?.setValue(year === null ? null : new Date(year, 0, 1));
    if (path === 'beraterSeit') {
      this.personalData.beraterSeit = year === null ? '' : year.toString();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onKeyPress(event: KeyboardEvent) {
    InputUtils.allowLettersOnly(event);
  }

  onSecurityChange(selectedCodes: string[]) {
    const securityArray = this.personalForm.get('securityCheck') as FormArray;
    const existingCodes = securityArray.controls.map(
      (ctrl) => ctrl.get('typ')?.value,
    );

    selectedCodes.forEach((code) => {
      if (!existingCodes.includes(code)) {
        securityArray.push(this.createSecurityCheckFormGroup({ typ: code }));
      }
    });

    const orderedCodes = this.securityOptions.map((opt) => opt.code);
    const sortedControls = orderedCodes
      .filter((code) => selectedCodes.includes(code))
      .map(
        (code) =>
          securityArray.controls.find(
            (ctrl) => ctrl.get('typ')?.value === code,
          )!,
      );

    securityArray.clear();
    sortedControls.forEach((ctrl) => securityArray.push(ctrl));

    this.personalForm.get('securityCheck')?.setValue(securityArray.value);
    this.personalForm.get('securityCheck')?.markAsDirty();

    if (this.editMode) {
      this.hasUnsavedChanges = true;
    }
  }

  getSecurityLabel(code: string): string {
    const option = this.securityOptions.find((opt) => opt.code === code);
    return option ? option.label : code;
  }

  isSecurityCheckExpired(
    completedOn: string | Date | null | undefined,
  ): boolean {
    // Delegate an das gemeinsame Utility, damit Badge-Zaehler und Formular
    // nicht auseinanderlaufen (Reviewer-Hinweis PR #617).
    return isSecurityCheckExpired(completedOn);
  }

  getRemainingMonths(completedOn: string | Date): number | null {
    if (!completedOn) return null;
    const expiry = new Date(completedOn);
    expiry.setMonth(expiry.getMonth() + 60);
    const now = new Date();
    if (expiry <= now) return null;
    const months =
      (expiry.getFullYear() - now.getFullYear()) * 12 +
      (expiry.getMonth() - now.getMonth());
    return months;
  }
}
